/**
 * Listings are a lightweight pattern for storing a subset of a list with pagination information
 */
import * as PropTypes from 'prop-types';

import conditionallyRequired from 'lib/conditionallyRequired';

export interface Pagination {
  offset: number;
  limit: number;
  total: number;
  isExact?: boolean;
}

const PAGINATION_PROPS = {
  offset: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  isExact: PropTypes.bool,
};

// A pagination object stores key state used for paging
export const PAGINATION_SHAPE = PropTypes.shape(PAGINATION_PROPS);

export interface ListingPagination extends Pagination {
  totalWithHidden: number;
}

// A pagination object has additional listing-specific keys
export const LISTING_PAGINATION_SHAPE = PropTypes.shape({
  ...PAGINATION_PROPS,
  totalWithHidden: PropTypes.number.isRequired,
});

const loadSuccess = (props: {loadSuccess: boolean}): boolean => props.loadSuccess;

export interface Listing<Result, Param> {
  isLoading: boolean;
  // A stale listing is one that has been explicitly marked as out-of-date but hasn't been
  // re-fetched. For example, if 10 workspace tabs have an active containers-for-batch listing and
  // we do an operation that affects the listings, we want to lazily reload the listings rather than
  // fetching them all right away, so we mark them as stale and let the components re-fetch as
  // necessary.
  isStale: boolean;
  loadSuccess: boolean;
  // A null `items` indicates that one more more of the items has been invalidated. This is a bit of a hack.
  // since we invalidate items by setting them to null without removing their ids from the store, a
  // component which uses the ids to get the items from the store will then receive a listing with some
  // null items. Instead we return a null for items indicating to the component to wait for a refresh.
  items: Array<Result> | null;
  pagination: ListingPagination | null;
  param: Param;
}

export const LISTING_SHAPE = PropTypes.shape({
  isLoading: PropTypes.bool.isRequired,
  isStale: PropTypes.bool.isRequired,
  loadSuccess: PropTypes.bool.isRequired,
  items: PropTypes.array,
  pagination: conditionallyRequired(LISTING_PAGINATION_SHAPE, loadSuccess),
  // eslint-disable-next-line react/forbid-prop-types
  param: PropTypes.any,
});

export function listingStart<Result, Param>(param: Param): Listing<Result, Param> {
  return {
    isLoading: true,
    isStale: false,
    loadSuccess: false,
    items: null,
    pagination: null,
    param,
  };
}

export function listingRestart<Result, Param>(listing: Listing<Result, Param>): Listing<Result, Param> {
  return {
    ...listing,
    isStale: false,
    isLoading: true,
  };
}

export function listingMarkStale<Result, Param>(listing: Listing<Result, Param>): Listing<Result, Param> {
  return {
    ...listing,
    isStale: true,
  };
}

export function listingComplete<Result, Param>(
  listing: Listing<Result, Param>,
  items: Array<Result>,
  pagination: ListingPagination
): Listing<Result, Param> {
  return {
    ...listing,
    isLoading: false,
    isStale: false,
    loadSuccess: true,
    items,
    pagination,
  };
}

export function listingFail<Result, Param>(listing: Listing<Result, Param>): Listing<Result, Param> {
  return {
    ...listing,
    isLoading: false,
    isStale: false,
    loadSuccess: false,
    items: null,
    pagination: null,
  };
}
